import { NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Input, Output, EventEmitter, Optional } from '@angular/core';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatNoDataRow,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Cart, CartActions, CartLineSources, CartSelectors, OrderTypes } from '@next/core-lib/cart';
import { AgrNotificationEmptyModule } from '@next/core-lib/components';
import { AbstractDialog, AbstractDialogData, DIALOG_RETURN_TYPES, AgrDialogModule } from '@next/core-lib/dialog';

import { AgrFormModule } from '@next/core-lib/form';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { TableV2Module } from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { addYears } from 'date-fns';
import { Subject, BehaviorSubject } from 'rxjs';

import { takeUntil, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { EmptyProductsDataSource, EmptyProduct } from './datasource/empty-products.datasource';
import { ReturnableEmptyProductsService } from './services/returnable-empty-products.service';

export interface ReturnEmptyProductDialogData extends AbstractDialogData {
  cart?: Cart;
}

type currentReturnCartItems = {
  skuId: number;
  invoiceId?: string;
  quantity: number;
  cartLineId: string;
  cartId: number;
}[];

@Component({
  selector: 'app-return-packaging-search-dialog',
  templateUrl: './return-packaging-search-dialog.component.html',
  styleUrls: ['./return-packaging-search-dialog.component.scss'],
  imports: [
    AgrDialogModule,
    MatFormField,
    MatLabel,
    MatIcon,
    MatPrefix,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatIconButton,
    MatSuffix,
    TableV2Module,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    AgrFormModule,
    MatButton,
    MatNoDataRow,
    AgrNotificationEmptyModule,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    AgrI18nSharedModule,
  ],
})
export class ReturnPackagingSearchDialogComponent extends AbstractDialog<ReturnEmptyProductDialogData> implements OnInit, OnDestroy {
  @Input() embedded = false;
  @Output() productSelected = new EventEmitter<{ product: EmptyProduct; quantity: number }>();

  searchBarControl = new FormControl('');
  quantityForms = new FormArray<FormControl<number>>([]);

  dataSource: EmptyProductsDataSource;
  tableColumns = ['product', 'quantity', 'addToCart'];

  private readonly destroy$ = new Subject<void>();
  private readonly _currentReturnCartItems = new BehaviorSubject<currentReturnCartItems>([]);

  public readonly productCount = new BehaviorSubject<number>(0);
  public readonly currentReturnCartItems$ = this._currentReturnCartItems.asObservable();
  public readonly dateRange = {
    start: addYears(new Date(), -1),
    end: new Date(),
  };

  constructor(
    @Optional() protected mdDialogRef: MatDialogRef<ReturnPackagingSearchDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ReturnEmptyProductDialogData,
    private readonly returnableEmptyProductsService: ReturnableEmptyProductsService,
    private readonly store: Store,
  ) {
    super();

    if (this.data) {
      this.data.title = 'product.return-empty-search.dialog-title';
      this.data.confirmButton = false;
    }

    this.dataSource = new EmptyProductsDataSource(this.returnableEmptyProductsService);
  }

  ngOnInit() {
    this.searchBarControl.valueChanges.pipe(distinctUntilChanged(), debounceTime(300), takeUntil(this.destroy$)).subscribe((searchTerm: string) => {
      this.dataSource.applyFilter(searchTerm?.trim() || '');
    });

    this.dataSource
      .connect()
      .pipe(takeUntil(this.destroy$))
      .subscribe((products: EmptyProduct[]) => {
        this.productCount.next(products.length);
        this.updateQuantityForms(products.length);
      });

    this.monitorCartState();
  }

  private updateQuantityForms(productCount: number) {
    while (this.quantityForms.length > 0) {
      this.quantityForms.removeAt(0);
    }

    for (let i = 0; i < productCount; i++) {
      this.quantityForms.push(new FormControl(1, { nonNullable: true }));
    }
  }

  clearSearchBar() {
    this.searchBarControl.setValue('');
  }

  addReturnLineToCart(row: EmptyProduct, index: number) {
    const newQuantity = this.quantityForms.at(index)?.value || 1;
    const currentCartItems = this._currentReturnCartItems.value;
    const existingItem = currentCartItems.find((item) => item.skuId === row.id);

    if (existingItem) {
      const totalQuantity = existingItem.quantity + newQuantity;
      this.store.dispatch(
        CartActions.updateCartLineQuantity({
          cartId: existingItem.cartId,
          cartLineId: existingItem.cartLineId,
          quantity: totalQuantity,
        }),
      );

      if (this.embedded) {
        this.productSelected.emit({ product: row, quantity: totalQuantity });
      } else {
        this.mdDialogRef?.close({ status: DIALOG_RETURN_TYPES.confirm, product: row, quantity: totalQuantity });
      }
    } else {
      if (row.maxReturnableQuantity && newQuantity > row.maxReturnableQuantity) {
        console.warn(`Quantity ${newQuantity} exceeds maximum returnable quantity ${row.maxReturnableQuantity}`);
        return;
      }

      this.store.dispatch(
        CartActions.addLineToReturnCart({
          cartLine: {
            skuId: row.id,
            quantity: newQuantity,
            externalInvoiceId: null,
            externalInvoiceLineId: null,
            source: CartLineSources.SHOP_RETURN_ORDER,
          },
        }),
      );

      if (this.embedded) {
        this.productSelected.emit({ product: row, quantity: newQuantity });
      } else {
        this.mdDialogRef?.close({ status: DIALOG_RETURN_TYPES.confirm, product: row, quantity: newQuantity });
      }
    }
  }

  getCurrentCartQuantity(skuId: number): number {
    const currentCartItems = this._currentReturnCartItems.value;
    const existingItem = currentCartItems.find((item) => item.skuId === skuId);
    return existingItem?.quantity || 0;
  }

  doesCartContainItem(row: EmptyProduct, currentReturnCartItems: currentReturnCartItems): boolean {
    return !!currentReturnCartItems.find((x) => x.skuId === row.id);
  }

  private monitorCartState() {
    this.store
      .select(CartSelectors.selectCurrentOrderCarts)
      .pipe(
        takeUntil(this.destroy$),
        map((carts) => {
          const allCurrentReturnCartItems: currentReturnCartItems = [];
          carts?.forEach((cart) => {
            if (cart.type === OrderTypes.returnableOrder) {
              cart.lines?.forEach((line) => {
                allCurrentReturnCartItems.push({
                  skuId: line.skuId,
                  invoiceId: line.externalInvoiceId || undefined,
                  quantity: line.quantity || 0,
                  cartLineId: line.id,
                  cartId: cart.id,
                });
              });
            }
          });

          return allCurrentReturnCartItems;
        }),
      )
      .subscribe((currentReturnCartItems) => {
        this._currentReturnCartItems.next(currentReturnCartItems);
      });
  }

  cancel() {
    if (!this.embedded && this.mdDialogRef) {
      this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.dataSource) {
      this.dataSource.disconnect(null);
    }
  }
}
